import * as THREE from 'three'
import { Mob } from './Mob'
import Terrain from '../terrain'
import { setBoxUVs, buildMinecraftUVs } from './utils'
import { InventoryItemType, ItemType } from '../items'

const textureLoader = new THREE.TextureLoader()
const px = 0.0625

const createTexturedMaterial = (texturePath: string) => {
  const texture = textureLoader.load(texturePath)
  texture.magFilter = THREE.NearestFilter
  texture.minFilter = THREE.NearestFilter

  return new THREE.MeshStandardMaterial({
    map: texture,
    side: THREE.FrontSide,
    roughness: 0.9,
    metalness: 0
  })
}

const createMat = (color: number) =>
  new THREE.MeshStandardMaterial({
    color,
    roughness: 0.92,
    metalness: 0
  })

const makeBox = (
  width: number,
  height: number,
  depth: number,
  material: THREE.Material,
  uvStart?: [number, number]
) => {
  const geometry = new THREE.BoxGeometry(px * width, px * height, px * depth)
  if (uvStart) {
    setBoxUVs(
      geometry,
      buildMinecraftUVs(uvStart[0], uvStart[1], width, height, depth)
    )
  }
  return new THREE.Mesh(geometry, material)
}

const makeLeg = (
  width: number,
  height: number,
  depth: number,
  material: THREE.Material,
  uvStart?: [number, number]
) => {
  const leg = makeBox(width, height, depth, material, uvStart)
  leg.geometry.translate(0, -px * height * 0.5, 0)
  return leg
}

const enableShadows = (mesh: THREE.Group) => {
  mesh.traverse(object => {
    if (object instanceof THREE.Mesh) {
      object.castShadow = true
      object.receiveShadow = true
    }
  })
}

export class Pig extends Mob {
  legFL: THREE.Mesh
  legFR: THREE.Mesh
  legBL: THREE.Mesh
  legBR: THREE.Mesh
  head: THREE.Mesh
  tail: THREE.Mesh
  animationPhase = 0

  constructor(scene: THREE.Scene, terrain: Terrain, position: THREE.Vector3, texturePath: string) {
    super(scene, terrain, position)
    this.speed = 1.1
    this.maxHealth = 10
    this.health = this.maxHealth

    const material = createTexturedMaterial(texturePath)

    const body = makeBox(10, 16, 8, material, [28, 8])
    body.rotation.x = Math.PI / 2
    body.position.set(0, px * 8, 0)
    this.mesh.add(body)

    this.head = makeBox(8, 8, 8, material, [0, 0])
    this.head.geometry.translate(0, px * 4, 0)
    this.head.position.set(0, px * 8, px * 6.8)
    this.mesh.add(this.head)

    const snout = makeBox(4, 3, 1, material, [16, 16])
    snout.position.set(0, px * 3.4, px * 4.45)
    this.head.add(snout)

    this.legFL = makeLeg(4, 6, 4, material, [0, 16])
    this.legFR = makeLeg(4, 6, 4, material, [0, 16])
    this.legBL = makeLeg(4, 6, 4, material, [0, 16])
    this.legBR = makeLeg(4, 6, 4, material, [0, 16])
    this.legFL.position.set(px * 3, px * 6, px * 4.8)
    this.legFR.position.set(-px * 3, px * 6, px * 4.8)
    this.legBL.position.set(px * 3, px * 6, -px * 4.8)
    this.legBR.position.set(-px * 3, px * 6, -px * 4.8)
    this.mesh.add(this.legFL, this.legFR, this.legBL, this.legBR)

    this.tail = new THREE.Mesh(
      new THREE.BoxGeometry(px * 1, px * 1, px * 4),
      createMat(0xf3a7b4)
    )
    this.tail.position.set(0, px * 9.5, -px * 8.3)
    this.tail.rotation.x = -0.8
    this.mesh.add(this.tail)

    this.headPart = this.head
    enableShadows(this.mesh)
  }

  updateAnimation(delta: number): void {
    this.animationPhase += delta * (this.state === 'walking' ? 8 : 2.2)

    if (this.state === 'walking') {
      const swing = Math.sin(this.animationPhase) * 0.45
      this.legFL.rotation.x = swing
      this.legBL.rotation.x = -swing
      this.legFR.rotation.x = -swing
      this.legBR.rotation.x = swing
      this.head.rotation.x = Math.sin(this.animationPhase * 0.8) * 0.06
      this.tail.rotation.y = Math.sin(this.animationPhase * 1.3) * 0.3
    } else {
      this.legFL.rotation.x = THREE.MathUtils.lerp(this.legFL.rotation.x, 0, delta * 5)
      this.legBL.rotation.x = THREE.MathUtils.lerp(this.legBL.rotation.x, 0, delta * 5)
      this.legFR.rotation.x = THREE.MathUtils.lerp(this.legFR.rotation.x, 0, delta * 5)
      this.legBR.rotation.x = THREE.MathUtils.lerp(this.legBR.rotation.x, 0, delta * 5)
      this.head.rotation.x = Math.sin(this.animationPhase) * 0.035
      this.tail.rotation.y = Math.sin(this.animationPhase * 1.1) * 0.14
    }
  }

  getDrops(): InventoryItemType[] {
    const drops: InventoryItemType[] = []
    const porkCount = 1 + Math.floor(Math.random() * 3)
    for (let i = 0; i < porkCount; i++) drops.push(ItemType.rawPorkchop)
    return drops
  }
}

export class Sheep extends Mob {
  legFL: THREE.Mesh
  legFR: THREE.Mesh
  legBL: THREE.Mesh
  legBR: THREE.Mesh
  head: THREE.Mesh
  animationPhase = 0
  grazeTimer = 0
  grazeCooldown = 5 + Math.random() * 8

  constructor(
    scene: THREE.Scene,
    terrain: Terrain,
    position: THREE.Vector3,
    texturePath: string,
    woolTexturePath: string
  ) {
    super(scene, terrain, position)
    this.speed = 0.95
    this.maxHealth = 8
    this.health = this.maxHealth

    const material = createTexturedMaterial(texturePath)
    const woolMaterial = createTexturedMaterial(woolTexturePath)

    const body = makeBox(8, 16, 6, woolMaterial, [28, 8])
    body.rotation.x = Math.PI / 2
    body.position.set(0, px * 11, 0)
    this.mesh.add(body)

    this.head = makeBox(6, 6, 8, material, [0, 0])
    this.head.geometry.translate(0, px * 3, 0)
    this.head.position.set(0, px * 11, px * 6.4)
    this.mesh.add(this.head)

    const woolCap = makeBox(7.5, 3, 7, woolMaterial, [0, 0])
    woolCap.position.set(0, px * 4.7, -px * 0.4)
    this.head.add(woolCap)

    this.legFL = makeLeg(4, 10, 4, material, [0, 16])
    this.legFR = makeLeg(4, 10, 4, material, [0, 16])
    this.legBL = makeLeg(4, 10, 4, material, [0, 16])
    this.legBR = makeLeg(4, 10, 4, material, [0, 16])
    this.legFL.position.set(px * 2.4, px * 9, px * 4)
    this.legFR.position.set(-px * 2.4, px * 9, px * 4)
    this.legBL.position.set(px * 2.4, px * 9, -px * 4)
    this.legBR.position.set(-px * 2.4, px * 9, -px * 4)
    this.mesh.add(this.legFL, this.legFR, this.legBL, this.legBR)

    this.headPart = this.head
    enableShadows(this.mesh)
  }

  updateAI(delta: number, canSwim = false) {
    // grazing: head down, standing still
    if (this.grazeTimer > 0) {
      this.grazeTimer -= delta
      this.velocity.x = 0
      this.velocity.z = 0
      this.state = 'idle'
      if (this.panicTimer > 0) this.grazeTimer = 0
      return
    }

    super.updateAI(delta, canSwim)

    if (
      this.state === 'idle' &&
      this.panicTimer <= 0 &&
      !this.temptTarget &&
      !this.isFollowing() &&
      !this.isBoarded
    ) {
      this.grazeCooldown -= delta
      if (this.grazeCooldown <= 0) {
        this.grazeTimer = 2
        this.grazeCooldown = 6 + Math.random() * 10
      }
    }
  }

  updateAnimation(delta: number): void {
    this.animationPhase += delta * (this.state === 'walking' ? 7 : 2)

    if (this.grazeTimer > 0) {
      this.head.rotation.x = THREE.MathUtils.lerp(this.head.rotation.x, 1.05, delta * 6)
      this.legFL.rotation.x = THREE.MathUtils.lerp(this.legFL.rotation.x, 0, delta * 5)
      this.legBL.rotation.x = THREE.MathUtils.lerp(this.legBL.rotation.x, 0, delta * 5)
      this.legFR.rotation.x = THREE.MathUtils.lerp(this.legFR.rotation.x, 0, delta * 5)
      this.legBR.rotation.x = THREE.MathUtils.lerp(this.legBR.rotation.x, 0, delta * 5)
      return
    }

    if (this.state === 'walking') {
      const swing = Math.sin(this.animationPhase) * 0.42
      this.legFL.rotation.x = swing
      this.legBL.rotation.x = -swing
      this.legFR.rotation.x = -swing
      this.legBR.rotation.x = swing
      this.head.rotation.x = Math.sin(this.animationPhase * 0.9) * 0.05
    } else {
      this.legFL.rotation.x = THREE.MathUtils.lerp(this.legFL.rotation.x, 0, delta * 5)
      this.legBL.rotation.x = THREE.MathUtils.lerp(this.legBL.rotation.x, 0, delta * 5)
      this.legFR.rotation.x = THREE.MathUtils.lerp(this.legFR.rotation.x, 0, delta * 5)
      this.legBR.rotation.x = THREE.MathUtils.lerp(this.legBR.rotation.x, 0, delta * 5)
      this.head.rotation.x = Math.sin(this.animationPhase * 0.85) * 0.03
    }
  }

  getDrops(): InventoryItemType[] {
    const drops: InventoryItemType[] = [ItemType.wool]
    const muttonCount = 1 + Math.floor(Math.random() * 2)
    for (let i = 0; i < muttonCount; i++) drops.push(ItemType.rawMutton)
    return drops
  }
}

const chickenBeakMat = createMat(0xf5a623)
const chickenWattleMat = createMat(0xd72723)

export class Chicken extends Mob {
  legL: THREE.Mesh
  legR: THREE.Mesh
  wingL: THREE.Mesh
  wingR: THREE.Mesh
  head: THREE.Mesh
  animationPhase = 0

  constructor(scene: THREE.Scene, terrain: Terrain, position: THREE.Vector3, texturePath: string) {
    super(scene, terrain, position)
    this.speed = 1.25
    this.maxHealth = 4
    this.health = this.maxHealth

    const material = createTexturedMaterial(texturePath)

    const body = makeBox(6, 8, 6, material, [0, 9])
    body.position.set(0, px * 7.5, 0)
    this.mesh.add(body)

    this.head = makeBox(4, 6, 4, material, [0, 0])
    this.head.position.set(0, px * 12.5, px * 4.1)
    this.mesh.add(this.head)

    const beak = makeBox(4, 2, 2, chickenBeakMat)
    beak.position.set(0, -px * 0.2, px * 3)
    this.head.add(beak)

    const wattle = makeBox(2, 2.2, 1, chickenWattleMat)
    wattle.position.set(0, -px * 2.4, px * 2.65)
    this.head.add(wattle)

    this.wingL = makeBox(1.2, 5, 5, material, [24, 13])
    this.wingR = makeBox(1.2, 5, 5, material, [24, 13])
    this.wingL.position.set(px * 3.7, px * 7.7, 0)
    this.wingR.position.set(-px * 3.7, px * 7.7, 0)
    this.mesh.add(this.wingL, this.wingR)

    this.legL = makeLeg(1, 5, 1, chickenBeakMat)
    this.legR = makeLeg(1, 5, 1, chickenBeakMat)
    this.legL.position.set(px * 1.8, px * 5, px * 1)
    this.legR.position.set(-px * 1.8, px * 5, px * 1)
    this.mesh.add(this.legL, this.legR)

    this.headPart = this.head
    enableShadows(this.mesh)
  }

  updateAnimation(delta: number): void {
    this.animationPhase += delta * (this.state === 'walking' ? 11 : 2.5)

    if (this.state === 'walking') {
      const swing = Math.sin(this.animationPhase) * 0.6
      this.legL.rotation.x = swing
      this.legR.rotation.x = -swing
      this.wingL.rotation.z = 0.18 + Math.sin(this.animationPhase * 1.4) * 0.18
      this.wingR.rotation.z = -0.18 - Math.sin(this.animationPhase * 1.4) * 0.18
      this.head.rotation.x = Math.sin(this.animationPhase * 0.85) * 0.1
    } else {
      this.legL.rotation.x = THREE.MathUtils.lerp(this.legL.rotation.x, 0, delta * 5)
      this.legR.rotation.x = THREE.MathUtils.lerp(this.legR.rotation.x, 0, delta * 5)
      this.wingL.rotation.z = THREE.MathUtils.lerp(this.wingL.rotation.z, 0.08, delta * 4)
      this.wingR.rotation.z = THREE.MathUtils.lerp(this.wingR.rotation.z, -0.08, delta * 4)
      this.head.rotation.x = Math.sin(this.animationPhase) * 0.06
    }
  }

  getDrops(): InventoryItemType[] {
    const drops: InventoryItemType[] = [ItemType.rawChicken]
    const featherCount = 1 + Math.floor(Math.random() * 2)
    for (let i = 0; i < featherCount; i++) drops.push(ItemType.feather)
    if (Math.random() < 0.45) drops.push(ItemType.egg)
    return drops
  }
}

const rabbitBodyMat = createMat(0x9a6a3f)
const rabbitDarkMat = createMat(0x5b3a22)
const rabbitBellyMat = createMat(0xe7d4b5)
const rabbitEyeMat = createMat(0x18110c)
const rabbitTailMat = createMat(0xf4eadc)

export class Rabbit extends Mob {
  bodyGroup: THREE.Group
  legFL: THREE.Mesh
  legFR: THREE.Mesh
  legBL: THREE.Mesh
  legBR: THREE.Mesh
  head: THREE.Mesh
  earL: THREE.Mesh
  earR: THREE.Mesh
  animationPhase = 0

  constructor(scene: THREE.Scene, terrain: Terrain, position: THREE.Vector3) {
    super(scene, terrain, position)
    this.speed = 1.45
    this.maxHealth = 4
    this.health = this.maxHealth

    this.bodyGroup = new THREE.Group()
    this.mesh.add(this.bodyGroup)

    const body = makeBox(5, 5, 8, rabbitBodyMat)
    body.position.set(0, px * 5.5, 0)
    this.bodyGroup.add(body)

    const belly = makeBox(4, 3.2, 1, rabbitBellyMat)
    belly.position.set(0, px * 5.2, px * 4.25)
    this.bodyGroup.add(belly)

    this.head = makeBox(5, 5, 5, rabbitBodyMat)
    this.head.position.set(0, px * 8.7, px * 5.2)
    this.bodyGroup.add(this.head)

    const eyeL = makeBox(0.6, 0.8, 0.35, rabbitEyeMat)
    const eyeR = makeBox(0.6, 0.8, 0.35, rabbitEyeMat)
    eyeL.position.set(-px * 1.5, px * 0.8, px * 2.7)
    eyeR.position.set(px * 1.5, px * 0.8, px * 2.7)
    this.head.add(eyeL, eyeR)

    this.earL = makeBox(1.3, 6, 1.2, rabbitDarkMat)
    this.earR = makeBox(1.3, 6, 1.2, rabbitDarkMat)
    this.earL.position.set(-px * 1.4, px * 13.4, px * 5.1)
    this.earR.position.set(px * 1.4, px * 13.4, px * 5.1)
    this.earL.rotation.x = -0.18
    this.earR.rotation.x = -0.18
    this.bodyGroup.add(this.earL, this.earR)

    const tail = makeBox(2.2, 2.2, 2.2, rabbitTailMat)
    tail.position.set(0, px * 6.1, -px * 4.8)
    this.bodyGroup.add(tail)

    this.legFL = makeLeg(1.4, 3.3, 1.4, rabbitDarkMat)
    this.legFR = makeLeg(1.4, 3.3, 1.4, rabbitDarkMat)
    this.legBL = makeLeg(2, 4, 2, rabbitDarkMat)
    this.legBR = makeLeg(2, 4, 2, rabbitDarkMat)
    this.legFL.position.set(px * 1.5, px * 3.4, px * 3)
    this.legFR.position.set(-px * 1.5, px * 3.4, px * 3)
    this.legBL.position.set(px * 1.8, px * 4, -px * 2.8)
    this.legBR.position.set(-px * 1.8, px * 4, -px * 2.8)
    this.bodyGroup.add(this.legFL, this.legFR, this.legBL, this.legBR)

    this.mesh.scale.setScalar(0.95)
    this.headPart = this.head
    enableShadows(this.mesh)
  }

  updateAnimation(delta: number): void {
    this.animationPhase += delta * (this.state === 'walking' ? 10 : 2.8)

    if (this.state === 'walking') {
      const hop = Math.max(0, Math.sin(this.animationPhase))
      const swing = Math.sin(this.animationPhase) * 0.55
      this.bodyGroup.position.y = hop * px * 1.5
      this.bodyGroup.rotation.x = hop * -0.08
      this.legFL.rotation.x = swing
      this.legFR.rotation.x = swing
      this.legBL.rotation.x = -swing * 0.75
      this.legBR.rotation.x = -swing * 0.75
      this.head.rotation.x = Math.sin(this.animationPhase * 0.9) * 0.08
    } else {
      this.bodyGroup.position.y = THREE.MathUtils.lerp(this.bodyGroup.position.y, 0, delta * 5)
      this.bodyGroup.rotation.x = THREE.MathUtils.lerp(this.bodyGroup.rotation.x, 0, delta * 5)
      this.legFL.rotation.x = THREE.MathUtils.lerp(this.legFL.rotation.x, 0, delta * 6)
      this.legFR.rotation.x = THREE.MathUtils.lerp(this.legFR.rotation.x, 0, delta * 6)
      this.legBL.rotation.x = THREE.MathUtils.lerp(this.legBL.rotation.x, 0, delta * 6)
      this.legBR.rotation.x = THREE.MathUtils.lerp(this.legBR.rotation.x, 0, delta * 6)
      this.head.rotation.x = Math.sin(this.animationPhase) * 0.04
    }

    const earTilt = Math.sin(this.animationPhase * 0.8) * 0.08
    this.earL.rotation.z = -0.08 + earTilt
    this.earR.rotation.z = 0.08 - earTilt
  }

  getDrops(): InventoryItemType[] {
    const drops: InventoryItemType[] = [ItemType.rawRabbit]
    if (Math.random() < 0.7) drops.push(ItemType.rabbitHide)
    if (Math.random() < 0.18) drops.push(ItemType.rabbitFoot)
    return drops
  }
}
